import { template as template_fd9b2463e5f141cfb5666b64daa1f11a } from "@ember/template-compiler";
import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { get } from '@ember/object';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import optional from '@nullvoxpopuli/ember-composable-helpers/helpers/optional';
import or from 'ember-truth-helpers/helpers/or';
import BvTooltip from 'volta/components/bv-tooltip';
import classNames from 'volta/helpers/class-names';
import type { ContentValue } from '@glint/template';
import type { IBvTableColumn, IBvTableRow } from 'volta/components/bv-table';
import type { BvTooltipSignature } from 'volta/components/bv-tooltip';
export interface CellBaseCellSignature<M = any, V = any> {
    Args: {
        cell: any;
        row: IBvTableRow;
        column: IBvTableColumn;
        isFooter?: boolean;
        scrollToRow: (rowModelId: string) => void;
    };
    Blocks: {
        default: [IBvTableBaseCellApi<M, V>];
    };
    Element: HTMLDivElement;
}
export interface IBvTableBaseCellValue<T = any> {
    cellClasses?: string;
    onClick?: (api: IBvTableBaseCellApi<T>) => void;
    tooltip?: string;
    tooltipSubtext?: string;
    tooltipContent?: BvTooltipSignature['Args']['content'];
}
export interface IBvTableBaseCellApi<T = any, V = any> {
    cell: any;
    model: T;
    value: V;
    column: IBvTableColumn;
    isSelected: boolean;
    scrollToRow: (rowModelId: string) => void;
}
export default class CellBaseCellComponent extends Component<CellBaseCellSignature> {
    get cell() {
        return this.args.cell;
    }
    get model() {
        return this.args.row.api.rowValue;
    }
    get isSelected() {
        return Boolean(this.args.row.api.rowMeta?.isSelected);
    }
    get value(): any {
        const { format, footerFormat, valuePath } = this.args.column;
        if (this.args.isFooter && typeof footerFormat === 'function') {
            return footerFormat(this.model, this.args.row);
        }
        if (!this.args.isFooter && typeof format === 'function') {
            return format(this.model, this.args.row);
        }
        return valuePath ? (get(this.model, valuePath) as ContentValue) : undefined;
    }
    static{
        template_fd9b2463e5f141cfb5666b64daa1f11a(`
    {{#let
      (hash
        cell=@cell
        model=@row.api.rowValue
        value=this.value
        column=@column
        isSelected=@row.api.rowMeta.isSelected
        scrollToRow=@scrollToRow
      )
      as |api|
    }}
      {{! template-lint-disable no-invalid-interactive }}
      <div
        class={{classNames
          'et-cell'
          @column.classNames
          api.value.cellClasses
          (if @column.showOnHover 'et-cell-show-on-row-hover')
        }}
        ...attributes
        {{on 'click' (fn (optional api.value.onClick) api)}}
      >
        {{#if (has-block)}}
          {{yield api}}
        {{else}}
          {{api.value}}
        {{/if}}
        {{#if (or api.value.tooltip api.value.tooltipContent api.value.tooltipSubtext)}}
          <BvTooltip @subtext={{api.value.tooltipSubtext}} @content={{api.value.tooltipContent}}>
            {{#if api.value.tooltip}}
              {{htmlSafe api.value.tooltip}}
            {{/if}}
          </BvTooltip>
        {{/if}}
      </div>
    {{/let}}
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
declare module '@glint/environment-ember-loose/registry' {
    export default interface Registry {
        'Cell::BaseCell': typeof CellBaseCellComponent;
        'cell/base-cell': typeof CellBaseCellComponent;
    }
}
